import { readPref } from '~/composables/usePersistedPref'

/**
 * A SOURCE is a kind of place a document lives: a client, a case, a sheet, a chat thread.
 *
 * ── WHY A SET AND NOT AN ENUM (AlexAmin/marcus#158) ────────────────────────────────────────────
 *
 * This was `GroupMode`, one of `folders | case | project | sheet | conversation`, driven by a
 * segmented control. A single enum cannot express "show me my sheets AND my conversations", which
 * is what the browser is actually asked for — these are sources a user combines, not modes that
 * exclude one another. So the state is a SET and the picker is a multi-select.
 *
 * ── AND FOLDERS IS NOT ONE OF THEM ─────────────────────────────────────────────────────────────
 *
 * The folder tree is a different kind of object: a path hierarchy each document has exactly one
 * position in, not a membership it can hold several of. Putting it in the same set would make
 * "folders + sheets" representable with no honest rendering of it.
 *
 * It is instead the EMPTY set — no source filter, so the browser falls back to the flat listing at
 * `currentPath`. One piece of state rather than two, and it keeps the folders/non-folders line every
 * reader branches on (`isFolderMode`) exactly as sharp as `groupMode === 'folders'` was, which is
 * what MAR-494's chat file filter depends on.
 */
export type DocumentSource = 'project' | 'case' | 'sheet' | 'conversation'

/**
 * Canonical render order, and the order the picker lists them in.
 *
 * Clientes before Casos (#81 — a client is the outer container, so offering the inner one first
 * asks the user to think from the inside out), then Hojas and Conversaciones last: both are homes
 * rather than legal matters, and a sheet is the more durable, named container of the two
 * (AlexAmin/marcus#150).
 */
export const DOCUMENT_SOURCES: readonly DocumentSource[] = ['project', 'case', 'sheet', 'conversation']

function isDocumentSource(v: unknown): v is DocumentSource {
  return typeof v === 'string' && (DOCUMENT_SOURCES as readonly string[]).includes(v)
}

/** Put a selection into canonical order — never rely on `Set` insertion order for rendering. */
export function orderSources(sources: Iterable<DocumentSource>): DocumentSource[] {
  const held = new Set(sources)
  return DOCUMENT_SOURCES.filter(s => held.has(s))
}

/** What a browser with no stored preference gets — grouping by CLIENT (#81). */
export const DEFAULT_SOURCES: readonly DocumentSource[] = ['project']
export const SOURCES_KEY = 'marcus:browser-sources'
/** The pre-#158 single-value key, read once so an existing choice survives the upgrade. */
const LEGACY_GROUP_MODE_KEY = 'marcus:browser-group-mode'

/**
 * The sources the user last chose, or `null` if they never chose any.
 *
 * Two keys are read, newest first. `marcus:browser-sources` is the JSON array #158 writes; the
 * single-valued `marcus:browser-group-mode` is what every existing browser holds, and is migrated on
 * READ rather than rewritten, so a user who never opens the picker again still keeps their choice.
 * `'matter'` is the pre-MAR-93 spelling of `'case'`, still in the localStorage of anyone who had the
 * sidebar open before that rename.
 *
 * An EMPTY selection is deliberately not restorable from either key: it is what a page IMPOSES (the
 * finder forcing its folder listing), never what the sidebar — which has no folder tree of its own —
 * should come back to.
 */
export function storedSources(): DocumentSource[] | null {
  const raw = readPref(SOURCES_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        const sources = orderSources(parsed.filter(isDocumentSource))
        if (sources.length > 0) return sources
      }
    } catch {
      // A corrupt value is the same as no value.
    }
  }
  const legacy = readPref(LEGACY_GROUP_MODE_KEY)
  if (legacy === 'matter' || legacy === 'case') return ['case']
  if (isDocumentSource(legacy)) return [legacy]
  return null
}
